import { useCallback, useEffect, useMemo, useState } from 'react';

// Manages audio input devices and a valid selected device id.
// Keeps selection valid when devices change and provides helpers to refresh and pick a fallback.
export default function useAudioDevices() {
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState(null);

  const enumerateAudioInputDevices = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'audioinput');
  }, []);

  const pickFallbackDeviceId = useCallback(async () => {
    try {
      const devices = await enumerateAudioInputDevices();
      if (devices.length === 0) return null;
      const defaultDevice = devices.find(device => device.deviceId === 'default') || devices[0];
      return defaultDevice.deviceId;
    } catch (err) {
      // Keep this hook UI-agnostic. Surface no errors here; caller can decide to handle.
      return null;
    }
  }, [enumerateAudioInputDevices]);

  const refreshDevices = useCallback(async () => {
    try {
      const audioInputDevices = await enumerateAudioInputDevices();
      setAudioDevices(audioInputDevices);

      const isCurrentDeviceAvailable = audioInputDevices.some(device => device.deviceId === selectedAudioDeviceId);

      if (audioInputDevices.length > 0) {
        if (!selectedAudioDeviceId || !isCurrentDeviceAvailable) {
          const defaultDevice = audioInputDevices.find(device => device.deviceId === 'default') || audioInputDevices[0];
          setSelectedAudioDeviceId(defaultDevice.deviceId);
        }
      } else {
        setSelectedAudioDeviceId(null);
      }
    } catch (err) {
      // Avoid tight coupling to error handlers or i18n; silently ignore here.
    }
  }, [enumerateAudioInputDevices, selectedAudioDeviceId]);

  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      // Initial population (will not include labels until permission is granted)
      refreshDevices();
    }
  }, [refreshDevices]);

  useEffect(() => {
    const handleDeviceChange = () => {
      refreshDevices();
    };
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }
    return () => {
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [refreshDevices]);

  return useMemo(() => ({
    audioDevices,
    selectedAudioDeviceId,
    setSelectedAudioDeviceId,
    refreshDevices,
    pickFallbackDeviceId,
  }), [audioDevices, selectedAudioDeviceId, refreshDevices, pickFallbackDeviceId]);
}


